import { memo } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link, useLocation, useParams } from 'react-router-dom'

import { CheckCircle } from '../../../components/Icons'
import { useBrand } from '../../../contexts/BrandContext'
import { useSession } from '../../../hooks/storeHooks/useSessionState'
import { analytics } from '../../../utils/analytics'

const nonAuthItems = [
  'Ask anything - companions can break down videos, articles, or complex topics',
  'We remember for you — pick up right where you left off',
  'File-smart answers — drop in docs and get instant insights',
]

const authItems = [
  'Unlimited sessions — chat for as long as you like',
  'Deep-dive help — get answers on any topic, video, or doc',
  'Memory that sticks — we remember past chats',
  'File-smart answers — ask about any upload',
]

interface OutOfMinutesModalProps {
  isSessionPage?: boolean
  question?: string
  onClose: () => void
}

const OutOfMinutesModal: React.FC<OutOfMinutesModalProps> = memo(
  ({ question, isSessionPage, onClose }) => {
    const { logo } = useBrand()
    const location = useLocation()
    const { slug } = useParams()
    const { isAuthenticated, signinRedirect } = useAuth()
    const { personaData } = useSession()

    const getReturnUrl = () => {
      if (question) {
        return `/search?query=${encodeURIComponent(question)}`
      }
      if (slug) {
        return `${location.pathname}${location.search || '?start=true'}`
      }
      return '/'
    }

    const handleLogin = () => {
      analytics.logEvent('aa_question_popup_login_btn_clicked')

      void signinRedirect({
        state: {
          returnUrl: getReturnUrl(),
        },
      })
    }

    const handleCreateAccount = async () => {
      analytics.logEvent(
        isSessionPage
          ? 'regular_session_popup_create_account_btn_clicked'
          : 'aa_question_popup_create_account_btn_clicked',
      )

      await signinRedirect({
        state: {
          returnUrl: getReturnUrl(),
        },
        extraQueryParams: {
          action: 'get-started',
          backgroundUrl: personaData?.videoLoopUrl || '',
        },
      })
    }

    const trackPricingClick = () => {
      analytics.logEvent(
        isSessionPage
          ? 'regular_session_popup_get_more_minutes_btn_clicked'
          : 'aa_question_popup_get_more_minutes_btn_clicked',
      )
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998]">
        <div
          className="tc-mv-blur rounded-regular p-6 max-w-lg w-full mx-4"
          data-testid="out-of-minutes-modal"
        >
          {!isSessionPage && (
            <button
              onClick={onClose}
              className="text-white absolute top-4 right-4 hover:text-white"
              data-testid="out-of-minutes-modal-close-btn"
              id="out-of-minutes-modal-close-btn"
            >
              <i className="fa-solid fa-xmark text-lg" />
            </button>
          )}
          <div className="flex flex-col justify-center items-center mb-4 text-center">
            <img alt="napster-logo" src={logo} className="h-[42px]" />
            <h2 className="text-white text-xl  mt-4">
              {isAuthenticated
                ? "You've used your free minutes..."
                : 'Keep the conversation going'}
            </h2>
          </div>
          <p className="text-white mt-4">
            {isAuthenticated
              ? "Your free time's up. Grab a plan or a minute pack to jump straight back in:"
              : 'Create a FREE account to unlock:'}
          </p>
          <ul className="pl-5 text-white mt-4 list-none !p-0">
            {(isAuthenticated ? authItems : nonAuthItems).map((item, index) => (
              <li key={index} className="mt-1 flex gap-2">
                <CheckCircle className="text-white min-w-4 mt-1" />
                {item}
              </li>
            ))}
          </ul>
          {isAuthenticated ? (
            <Link
              to="/pricing"
              className="btn btn-success mx-auto mt-4 w-fit"
              data-testid="out-of-minutes-modal-buy-minutes"
              id="out-of-minutes-modal-buy-minutes"
              onClick={trackPricingClick}
            >
              Get more minutes
            </Link>
          ) : (
            <div className="flex  justify-center gap-4">
              {!isSessionPage && (
                <button
                  className="btn btn-success !bg-blue mt-4 w-fit"
                  data-testid="out-of-minutes-modal-login"
                  id="out-of-minutes-modal-login"
                  onClick={handleLogin}
                >
                  Log In
                </button>
              )}
              <button
                className="btn btn-success mt-4 w-fit"
                data-testid="create-free-account-button"
                id="out-of-minutes-modal-create-acc"
                onClick={handleCreateAccount}
              >
                Create FREE account
              </button>
            </div>
          )}
        </div>
      </div>
    )
  },
)

export { OutOfMinutesModal }
